import React from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonButton,
  IonText,
  IonItem,
  IonList,
  IonListHeader,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonIcon,
} from '@ionic/react';
import { addOutline, radioOutline, refreshOutline, wifiOutline } from 'ionicons/icons';
import { useHubDetail } from '../../application/use-cases/hubs/useHubDetail';
import { wifiQuality } from '../../domain/Hub';
import { formatRelative } from '../../domain/WeatherStation';
import './Sensors.css';

const HubDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hub, stations, sites, loading, error, message, busy, changeSite, restart, openWifiSetup, addStation, goToStation } =
    useHubDetail(id);
  const online = hub?.status === 'online';
  const wifi = wifiQuality(hub?.wifiRssi ?? null);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/devices" />
          </IonButtons>
          <IonTitle>Conexión del campo</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center">
            <IonSpinner color="primary" />
          </div>
        )}

        {hub && (
          <>
            <div className="hub-summary">
              <h2>{hub.siteName ?? 'Hub sin campo'}</h2>
              <p>
                <span className={`sensor-card-status ${online ? 'is-online' : 'is-offline'}`}>
                  <span className="dot" />
                  {online ? 'En línea' : 'Sin conexión'}
                </span>
                {hub.lastSeenAt && <span> · último contacto {formatRelative(hub.lastSeenAt)}</span>}
              </p>
              <p className="hub-summary-meta">
                {hub.id}
                {wifi && ` · WiFi ${wifi} (${hub.wifiRssi} dBm)`}
                {hub.firmware && ` · firmware ${hub.firmware}`}
              </p>
              {!online && (
                <IonText color="medium">
                  <p style={{ fontSize: 13 }}>
                    Sin el hub no llegan los datos de ninguna estación de este campo. Revisá que tenga luz y que el WiFi del
                    casco ande; si cambió la clave, el hub abre su red de instalación solo a los 5 minutos.
                  </p>
                </IonText>
              )}
            </div>

            <IonList inset>
              <IonItem>
                <IonSelect
                  label="Campo"
                  labelPlacement="stacked"
                  placeholder="Sin campo"
                  value={hub.siteId}
                  disabled={busy}
                  onIonChange={(e) => changeSite(e.detail.value ?? null)}
                >
                  <IonSelectOption value={null}>Sin campo</IonSelectOption>
                  {sites.map((s) => (
                    <IonSelectOption key={s.id} value={s.id}>
                      {s.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>

            <IonList inset>
              <IonListHeader>
                <IonLabel>Estaciones ({stations.length})</IonLabel>
              </IonListHeader>
              {stations.map((s) => (
                <IonItem key={s.id} button detail onClick={() => goToStation(s.id)}>
                  <IonIcon icon={radioOutline} slot="start" />
                  <IonLabel>
                    <h3>{s.name}</h3>
                    <p>{s.status === 'online' ? 'Online' : 'Sin conexión'}</p>
                  </IonLabel>
                </IonItem>
              ))}
              <IonItem button detail={false} onClick={addStation} disabled={!online}>
                <IonIcon icon={addOutline} slot="start" color="primary" />
                <IonLabel color="primary">Agregar estación</IonLabel>
              </IonItem>
            </IonList>

            <IonList inset>
              <IonListHeader>
                <IonLabel>Sin abrir la caja</IonLabel>
              </IonListHeader>
              <IonItem button detail={false} onClick={openWifiSetup} disabled={busy || !online}>
                <IonIcon icon={wifiOutline} slot="start" />
                <IonLabel>
                  <h3>Cambiar el WiFi</h3>
                  <p>Abre la red de instalación del hub por 5 minutos</p>
                </IonLabel>
              </IonItem>
              <IonItem button detail={false} onClick={restart} disabled={busy || !online}>
                <IonIcon icon={refreshOutline} slot="start" />
                <IonLabel>
                  <h3>Reiniciar el hub</h3>
                  <p>Si algo no anda, vuelve en menos de un minuto</p>
                </IonLabel>
              </IonItem>
            </IonList>
          </>
        )}

        {message && (
          <IonText color="success">
            <p style={{ fontSize: 13 }}>{message}</p>
          </IonText>
        )}
        {error && (
          <IonText color="danger">
            <p style={{ fontSize: 13 }}>{error}</p>
          </IonText>
        )}
        {!loading && !hub && (
          <IonButton expand="block" fill="clear" routerLink="/app/devices">
            Volver a Sensores
          </IonButton>
        )}
      </IonContent>
    </IonPage>
  );
};

export default HubDetail;
